import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import Btn from '../../../../../../../components/Buttons/Btn';
import CreateMotive from '../../../Page1/Components/CreateMotive';
import AddBtn from '../../../../../../../components/Buttons/AddBtn';
import Modal from '../../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../../components/Notification';
import TablePaginator from '../../../../../../../components/TablePaginator';
import { ActionButtonsContainer } from '../../../../../../../components/TablePaginator/styles';
import SingleSelect from '../../../../../../../components/SingleSelect';
import HttpStatus from '../../../../../../../enums/httpStatus';
import i18n from '../../../../../../../i18n';
import { DeleteIcon } from '../../../../../../../icons';
import {
  actionsReq,
  motivesByActionsReq,
  motivesReq,
} from '../../../../../../../services/requests';
import * as TP from '../../../../../../../types/apiResponseTypes';
import * as s from '../styles';

type Props = {
  closeModal: () => void;
  reqGetAllMotiveByActions: () => void;
  selectedItem: TP.MotiveByAction | null;
  usedActionIds: number[];
};

type Option = {
  value: number;
  label: string;
};

type MotiveRow = {
  id: number;
  description: string;
};

const CreateOrEditMotiveByAction = ({
  closeModal,
  reqGetAllMotiveByActions,
  selectedItem,
  usedActionIds,
}: Props) => {
  const { t } = useTranslation();
  const isEdit = selectedItem !== null;

  const [showCreateMotive, setShowCreateMotive] = useState(false);
  const [waitingReq, setWaitingReq] = useState(false);
  const [motives, setMotives] = useState<TP.Motive[]>([]);
  const [actions, setActions] = useState<TP.Action[]>([]);
  const [actionSelected, setActionSelected] = useState<Option | null>(
    selectedItem
      ? { value: selectedItem.actionId, label: selectedItem.actionLabel }
      : null,
  );
  const [motiveSelected, setMotiveSelected] = useState<Option | null>(null);
  const [selectedMotives, setSelectedMotives] = useState<MotiveRow[]>(
    selectedItem?.motives ?? [],
  );

  const actionAlreadyUsed =
    actionSelected !== null &&
    usedActionIds.includes(actionSelected.value) &&
    actionSelected.value !== selectedItem?.actionId;

  const reqGetData = async () => {
    setWaitingReq(true);

    const motiveSource = motivesReq.axios.CancelToken.source();
    const actionSource = actionsReq.axios.CancelToken.source();

    try {
      const [motivesRes, actionsRes] = await Promise.all([
        motivesReq.index(motiveSource.token),
        actionsReq.index(actionSource.token),
      ]);

      if (motivesRes.status !== HttpStatus.OK) {
        throw motivesRes;
      }

      if (actionsRes.status !== HttpStatus.OK) {
        throw actionsRes;
      }

      setMotives(motivesRes.data.data || []);
      setActions(actionsRes.data.data || []);
    } catch (err) {
      if (!motivesReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setWaitingReq(false);
    }

    return () => {
      motiveSource.cancel('Component got unmounted');
      actionSource.cancel('Component got unmounted');
    };
  };

  useEffect(() => {
    reqGetData();
  }, []);

  const handleAddMotive = () => {
    if (motiveSelected === null) {
      frontendNotification({
        message: t('selectMotive'),
        type: 'warning',
      });
      return;
    }

    if (selectedMotives.some((motive) => motive.id === motiveSelected.value)) {
      frontendNotification({
        message: t('alreadyExists'),
        type: 'warning',
      });
      return;
    }

    const selectedMotive = motives.find((motive) => motive.id === motiveSelected.value);

    if (!selectedMotive) {
      return;
    }

    setSelectedMotives((currentMotives) => [
      ...currentMotives,
      {
        id: selectedMotive.id,
        description: selectedMotive.description,
      },
    ]);
    setMotiveSelected(null);
  };

  const handleMotiveCreated = (motive: TP.Motive) => {
    setMotives((current) => [...current, motive]);
    setSelectedMotives((current) => [...current, { id: motive.id, description: motive.description }]);
  };

  const removeMotive = (row: Row<MotiveRow>) => {
    setSelectedMotives((currentMotives) =>
      currentMotives.filter((motive) => motive.id !== row.original.id),
    );
  };

  const reqSave = async () => {
    if (actionAlreadyUsed || showCreateMotive) {
      return;
    }

    if (actionSelected === null) {
      frontendNotification({
        message: t('selectAllFields'),
        type: 'warning',
      });
      return;
    }

    if (selectedMotives.length === 0) {
      frontendNotification({
        message: t('selectMotive'),
        type: 'warning',
      });
      return;
    }

    setWaitingReq(true);

    try {
      const res = await motivesByActionsReq.store({
        action_id: actionSelected.value,
        ...(selectedItem ? { original_action_id: selectedItem.actionId } : {}),
        motive_ids: selectedMotives.map((motive) => motive.id),
      });

      if (res.status === HttpStatus.OK) {
        frontendNotification({
          message: t(isEdit ? 'successfullyUpdated' : 'successfullyCreated'),
          type: 'success',
        });
        reqGetAllMotiveByActions();
        closeModal();
      } else {
        throw res;
      }
    } catch (err) {
      if (!motivesByActionsReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setWaitingReq(false);
    }
  };

  const actionOptions = actions.map((action) => ({
    label: action.label,
    value: action.id,
  }));

  const motiveOptions = motives
    .filter((motive) => !selectedMotives.some((selected) => selected.id === motive.id))
    .map((motive) => ({
      label: motive.description,
      value: motive.id,
    }));

  const columns: Column<MotiveRow>[] = useMemo(
    () => [
      {
        Header: t('motive') as string,
        accessor: 'description',
      },
      {
        id: 'actions',
        Cell: ({ row }: any) => (
          <ActionButtonsContainer type={3}>
            <DeleteIcon
              size="1.5rem"
              title={t('delete')}
              onClick={() => removeMotive(row)}
            />
          </ActionButtonsContainer>
        ),
      },
    ],
    [i18n.language, selectedMotives],
  );

  return (
    <>
      <Modal
        title={t(isEdit ? 'editMotivesByAction' : 'newMotivesByAction')}
        showModal
        closeModal={closeModal}
        cancelButton={closeModal}
        action={() => reqSave()}
        isLoading={waitingReq}
        disableActionBtn={actionAlreadyUsed || showCreateMotive}
        style={{ minWidth: 'min(760px, 85vw)' }}
        bodyStyle={selectedMotives.length >= 3 ? { overflowX: 'auto' } : {}}
      >
        <s.Container>
          <s.Line>
            <s.Item>
              <p>{t('action')}</p>
              <SingleSelect
                value={actionSelected}
                options={actionOptions}
                onChange={(option: Option | null) => setActionSelected(option)}
                disabled={waitingReq}
                style={{ width: '100%' }}
              />
              {actionAlreadyUsed && (
                <s.FieldError role="alert">{t('actionAlreadyHasMotives')}</s.FieldError>
              )}
            </s.Item>
          </s.Line>

          <s.Line>
            <s.Item>
              <p>{t('motive')}</p>
              <SingleSelect
                value={motiveSelected}
                options={motiveOptions}
                onChange={(option: Option | null) => setMotiveSelected(option)}
                disabled={waitingReq}
                style={{ width: '100%' }}
              />
            </s.Item>

            <s.Item className="add-btn">
              <AddBtn onClick={handleAddMotive} disabled={waitingReq} />
            </s.Item>
            <s.Item style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
              <Btn
                text={t('newMotive')}
                type="outlined"
                disabled={waitingReq}
                onClick={() => setShowCreateMotive(true)}
              />
            </s.Item>
          </s.Line>

          <s.Line>
            <TablePaginator
              data={selectedMotives}
              columns={columns}
              globalFiltering
              style={{ width: '100%', marginTop: '2rem' }}
            />
          </s.Line>
        </s.Container>
      </Modal>
      {showCreateMotive && (
        <CreateMotive closeModal={() => setShowCreateMotive(false)} onCreated={handleMotiveCreated} />
      )}
    </>
  );
};

export default CreateOrEditMotiveByAction;
